// Machines (stage 4, EnderIO-flavoured): what each one holds, burns and makes. Data only; the server
// runs them (server/machines.ts) and the client labels its panel from the same tables.
import type { ItemId } from './items.ts';

export type MachineKind = 'furnace' | 'generator' | 'sag_mill' | 'alloy' | 'still' | 'tap' | 'harvester' | 'alternator';

export type MachineDef = {
  kind: MachineKind;
  name: string;
  item: ItemId; // the placeable item
  /** Slot labels, in order. */
  slots: string[];
  /** Which slots the machine fills itself from (inputs/fuel) and empties into chests (outputs). */
  inputs: number[];
  fuel?: number; // index of the fuel slot
  output: number;
  /** Power drawn per second while working (0 = burns its own fuel instead). */
  power: number;
  blurb: string;
};

export const MACHINES: Record<MachineKind, MachineDef> = {
  furnace: { kind: 'furnace', name: 'Stone furnace', item: 'stone_furnace', slots: ['Ore or dust', 'Fuel', 'Out'], inputs: [0], fuel: 1, output: 2, power: 0, blurb: 'Burns fuel to smelt ore or dust into ingots (wood becomes charcoal).' },
  generator: { kind: 'generator', name: 'Generator', item: 'generator', slots: ['Fuel'], inputs: [], fuel: 0, output: -1, power: 0, blurb: 'Burns fuel into power for machines joined to it by energy cables. Item pipes bring it fuel.' },
  sag_mill: { kind: 'sag_mill', name: 'SAG mill', item: 'sag_mill', slots: ['Ore', 'Out'], inputs: [0], output: 1, power: 15, blurb: 'Grinds one ore into two dust: twice the metal. Needs power.' },
  alloy: { kind: 'alloy', name: 'Alloy smelter', item: 'alloy_smelter', slots: ['In', 'In', 'Out'], inputs: [0, 1], output: 2, power: 20, blurb: 'Makes alloys, and smelts ore or dust without fuel. Needs power.' },
  still: { kind: 'still', name: 'Still', item: 'still', slots: ['Resin or produce', 'Out'], inputs: [0], output: 1, power: 10, blurb: 'Distils resin into tree oil, and presses pumpkins or squash into plant oil. Both burn long in a generator. Needs power.' },
  tap: { kind: 'tap', name: 'Resin tap', item: 'resin_tap', slots: ['Resin'], inputs: [], output: 0, power: 0, blurb: 'Drips resin from the trees around it: the more trees within 2 tiles, the faster. Join it to a chest or a still with item pipes.' },
  harvester: { kind: 'harvester', name: 'Harvester', item: 'harvester', slots: [], inputs: [], output: -1, power: 8, blurb: 'Picks ripe crops within 3 tiles, replants them, and sends the produce into its network. Needs power and storage on its item pipes.' },
  alternator: { kind: 'alternator', name: 'Alternator', item: 'alternator', slots: [], inputs: [], output: -1, power: 0, blurb: 'Turns the water wheels and windmills touching it into power for its cable network: free, small and steady.' },
};

/** Seconds of burn per fuel item. */
export const FUEL: Partial<Record<ItemId, number>> = { wood: 10, darkwood: 12, charwood: 14, charcoal: 40, coal: 60, tree_oil: 90, plant_oil: 90 };

/** Still: what it turns into oil. */
export const STILL: Partial<Record<ItemId, ItemId>> = { resin: 'tree_oil', pumpkin: 'plant_oil', squash: 'plant_oil' };
export const STILL_SECS = 6;
/** Resin tap: seconds per resin is TAP_SECS / (1 + 0.5 per tree within 2 tiles); nothing with no trees. */
export const TAP_SECS = 240;
export const TAP_RADIUS = 2;
/** Harvester: one ripe crop picked every HARVEST_SECS of powered work, within HARVEST_RADIUS tiles. */
export const HARVEST_SECS = 3;
export const HARVEST_RADIUS = 3;
/** Alternator: power per second per touching water wheel / windmill (windmills x2 in the Highlands). */
export const WHEEL_POWER = 4;
export const WINDMILL_POWER = 4;
export const ALT_CAP = 2000;

/** Ore or dust -> ingot (furnace, or the alloy smelter with a single input). */
export const SMELT: Partial<Record<ItemId, ItemId>> = {
  copper_ore: 'copper_ingot', tin_ore: 'tin_ingot', iron_ore: 'iron_ingot', silver_ore: 'silver_ingot', gold_ore: 'gold_ingot',
  copper_dust: 'copper_ingot', tin_dust: 'tin_ingot', iron_dust: 'iron_ingot', silver_dust: 'silver_ingot', gold_dust: 'gold_ingot',
};

export const MILL: Partial<Record<ItemId, ItemId>> = {
  copper_ore: 'copper_dust', tin_ore: 'tin_dust', iron_ore: 'iron_dust', silver_ore: 'silver_dust', gold_ore: 'gold_dust',
};

/** What an alloy recipe counts: copper ore, copper dust and a copper ingot are all 'copper'. */
export function metalOf(id: ItemId): string {
  const m = /^(copper|tin|iron|silver|gold)_(ore|dust|ingot)$/.exec(id);
  return m ? m[1] : id;
}

/** Alloy smelter recipes: two inputs (either order, metal in any form) -> output. */
export const ALLOYS: { a: [string, number]; b: [string, number]; out: [ItemId, number] }[] = [
  { a: ['copper', 3], b: ['tin', 1], out: ['bronze_ingot', 4] },
  { a: ['iron', 2], b: ['coal', 1], out: ['steel_ingot', 2] },
  { a: ['silver', 1], b: ['gold', 1], out: ['electrum_ingot', 2] },
  { a: ['dark_ore', 2], b: ['obsidian', 1], out: ['dark_steel_ingot', 3] },
];

export const SMELT_SECS = 8;
export const MILL_SECS = 6;
export const ALLOY_SECS = 8;
export const GEN_RATE = 25; // power per second while burning
export const GEN_CAP = 4000; // power a generator can hold

// ---------------------------------------------------------------- storage (stage 6, Refined Storage-flavoured)

/** Items a drive holds, any mix of kinds. A core takes CORE_DRIVES of them. */
export const DRIVES: Partial<Record<ItemId, number>> = { storage_drive: 1000, large_storage_drive: 5000 };
export const CORE_DRIVES = 4;
